// src/api/docs.js
import { supabase } from '../supabaseClient';
import { sanitizeInputByType, sanitizeStructuredInput } from '../helpers/formatHelpers';

const fnv1aHash = (value) => {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
};

export const computeDocFingerprint = (contentJson) => {
  if (!contentJson || typeof contentJson !== 'object') return null;
  try {
    if (Array.isArray(contentJson.chunks)) {
      const canonicalRefs = contentJson.chunks.map(ref => ({
        chunk_hash: String(ref?.chunk_hash ?? ''),
        hash: String(ref?.hash ?? ''),
        char_length: Number(ref?.char_length || 0)
      }));
      return fnv1aHash(JSON.stringify(canonicalRefs));
    }
    return fnv1aHash(JSON.stringify(contentJson));
  } catch (err) {
    return null;
  }
};

// Pure DB calls only

export async function apiGetDocs(projectId) {
  // Get project_docs entries
  const { data: projectDocs, error: projectDocsError } = await supabase
    .from('project_docs')
    .select('doc_id, parent_id')
    .eq('project_id', projectId)
    .order('doc_id', { ascending: true });

  if (projectDocsError || !projectDocs?.length) return [];

  // Get all doc IDs
  const docIds = projectDocs.map(pd => pd.doc_id);

  // Fetch actual docs
  const { data: docs, error: docsError } = await supabase
    .from('docs')
    .select('id, name, content, layout, created_by, updated_by, created_at, updated_at, type')
    .in('id', docIds);

  if (docsError || !docs) return [];

  // Combine the data
  return projectDocs.map(pd => {
    const doc = docs.find(d => d.id === pd.doc_id);
    return {
      id: doc?.id,
      name: doc?.name,
      content: doc?.content,
      layout: doc?.layout,
      created_by: doc?.created_by,
      updated_by: doc?.updated_by,
      created_at: doc?.created_at,
      updated_at: doc?.updated_at,
      parent_id: pd.parent_id,
      doc_id: pd.doc_id,
      type: 'doc',
      docType: doc?.type ?? null
    };
  });
}


export async function apiCreateDoc(projectId, name, content, _userId, parentId = null, docType = null) {
  try {
    const safeName = sanitizeInputByType(name, 'title', { maxLength: 180 }) || 'Untitled';
    const safeContent = sanitizeInputByType(content ?? '', 'text');
    const { data, error } = await supabase.rpc('create_doc', {
      p_project_id: projectId,
      p_name: safeName,
      p_content: safeContent,
      p_parent_id: parentId ?? null,
      p_type: docType === 'chat' ? 'chat' : null
    });

    if (error) throw error;
    return Array.isArray(data) ? data[0] ?? null : data;
  } catch (err) {
    throw err;
  }
}

export async function apiAddDoc(projectId, docId, parentId = null) {
  const { data, error } = await supabase
    .from('project_docs')
    .insert([{ project_id: projectId, doc_id: docId, parent_id: parentId }])
    .select()
    .single();
  if (error) throw error;
  return data;
}

// Delete a doc from the DB entirely
export async function apiDeleteDoc(docId) {
  const { data, error } = await supabase
    .from('docs')
    .delete()
    .eq('id', docId)
    .select('id')
    .single();
  if (error) throw error;
  return data;
}

// Remove a doc from a specific project only
export async function apiRemoveDoc(projectId, docId) {
  try {
    const { data, error } = await supabase.rpc('remove_doc_from_project', {
      p_project_id: projectId,
      p_doc_id: docId
    });

    if (error) throw error;
    return data;
  } catch (err) {
    throw err;
  }
}

// Get all projects that reference a doc
export async function apiGetProjectsForDoc(docId) {
  const { data, error } = await supabase
    .from('project_docs')
    .select('project_id')
    .eq('doc_id', docId);
  if (error) throw error;
  return data || [];
}

export async function apiLoadDoc(docId) {
  const { data, error } = await supabase
    .from('docs')
    .select('content, content_json, layout, name, updated_at')
    .eq('id', docId)
    .single();
  if (error) throw error;
  return data || { content: '', content_json: null, layout: null, name: '', updated_at: null };
}

export async function apiLoadDocMeta(docId) {
  const { data, error } = await supabase
    .from('docs')
    .select('layout, name, updated_at, content_json')
    .eq('id', docId)
    .single();
  if (error) throw error;
  const fingerprint = computeDocFingerprint(data?.content_json || null);
  return {
    layout: data?.layout ?? null,
    name: data?.name ?? '',
    updated_at: data?.updated_at ?? null,
    fingerprint
  };
}

export async function apiSaveDoc(docId, content, userId, options = {}) {
  const updates = {
    content: sanitizeInputByType(content, 'text'),
    updated_by: userId,
    updated_at: new Date().toISOString()
  };

  if (options.chunk_count !== undefined) {
    updates.chunk_count = options.chunk_count;
  }

  if (options.content_json !== undefined) {
    updates.content_json = options.content_json;
  }

  const { data, error } = await supabase
    .from('docs')
    .update(updates)
    .eq('id', docId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function apiRenameDoc(docId, newName) {
  const safeName = sanitizeInputByType(newName, 'title', { maxLength: 180 }) || 'Untitled';
  const { data, error } = await supabase.rpc('rename_doc', {
    p_doc_id: docId,
    p_name: safeName
  });
  if (error) throw error;
  return data;
}

export async function apiUpdateDocLayout(docId, layout) {
  const { data, error } = await supabase.rpc('update_doc_layout', {
    p_doc_id: docId,
    p_layout: layout === 'pageless' ? null : layout
  });
  if (error) throw error;
  return data;
}

export async function apiUpdateDocMetadata(docId, metadata) {
  const safeMetadata = sanitizeStructuredInput(metadata, { maxDepth: 10, maxStringLength: 3000 });
  // Future metadata fields can be added here if needed
  const { data, error } = await supabase.rpc('update_doc_metadata', {
    p_doc_id: docId,
    p_metadata: safeMetadata
  });
  if (error) throw error;
  return data;
}


export async function apiMoveDoc(docId, newParentId) { 
  const { data, error } = await supabase.rpc('move_doc', {
    p_doc_id: docId,
    p_parent_id: newParentId ?? null
  });

  if (error) throw error;
  return data;
}



